import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, Briefcase, Edit, Trash2, Upload } from 'lucide-react';
import { directorsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { toast } from 'sonner';
import logger from '@/lib/logger';
import DirectorFormDialog from './components/DirectorFormDialog';
import BulkImportDirectorsDialog from './components/BulkImportDirectorsDialog';

export default function DirectorsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [directors, setDirectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editingDirector, setEditingDirector] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await directorsAPI.list();
      setDirectors(res.data || []);
    } catch (err) {
      logger.error('Failed to load directors', err);
      toast.error('Failed to load directors');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = directors.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (d.name || '').toLowerCase().includes(q)
      || (d.din || '').toLowerCase().includes(q)
      || (d.pan || '').toLowerCase().includes(q)
      || (d.email || '').toLowerCase().includes(q);
  });

  const handleDelete = async (director) => {
    if (!window.confirm(`Delete director "${director.name}" (DIN ${director.din})? All client linkages will be removed.`)) return;
    try {
      await directorsAPI.delete(director.id);
      toast.success('Director deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const openEdit = (director) => {
    setEditingDirector(director);
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditingDirector(null);
    setDialogOpen(true);
  };

  const handleSaved = () => {
    setDialogOpen(false);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Briefcase className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Directors / Designated Partners</h1>
            <p className="text-xs text-muted-foreground">Master database of directors (Pvt Ltd) and designated partners (LLP)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-import-btn">
              <Upload className="h-4 w-4 mr-1.5" /> Bulk Import
            </Button>
          )}
          <Button onClick={openCreate} data-testid="add-director-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Director
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, DIN, PAN, or email…"
              className="pl-9"
              data-testid="directors-search"
            />
          </div>

          <div className="text-xs text-muted-foreground">
            {filtered.length} of {directors.length} director(s)
          </div>

          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {directors.length === 0 ? 'No directors yet. Click "Add Director" to create one.' : 'No directors match your search.'}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[28%]">Name</TableHead>
                    <TableHead>DIN</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>PAN</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-center">Companies</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(d => (
                    <TableRow
                      key={d.id}
                      className="cursor-pointer hover:bg-muted/30"
                      onClick={() => navigate(`/dashboard/directors/${d.id}`)}
                      data-testid={`director-row-${d.id}`}
                    >
                      <TableCell className="font-medium">{d.name}</TableCell>
                      <TableCell className="font-mono text-xs">{d.din}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-[10px]">{d.type || 'Director'}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{d.pan || '-'}</TableCell>
                      <TableCell className="text-xs">{d.email || '-'}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-xs">{d.client_count || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {d.active === false ? (
                          <Badge variant="outline" className="text-[10px] text-muted-foreground">Inactive</Badge>
                        ) : (
                          <Badge className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(d)} data-testid={`edit-director-${d.id}`}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500" onClick={() => handleDelete(d)} data-testid={`delete-director-${d.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <DirectorFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        director={editingDirector}
        onSaved={handleSaved}
      />

      <BulkImportDirectorsDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onImported={load}
      />
    </div>
  );
}
